// src/pages/CheckoutPayment.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { OrderSummary } from "./CheckoutAddress";
import "./CheckoutPage.css";

const PAYMENT_METHODS = [
  { id: "upi", label: "UPI", icon: "💳", desc: "Pay via GPay, PhonePe, Paytm" },
  { id: "card", label: "Credit / Debit Card", icon: "💳", desc: "Visa, MasterCard, RuPay" },
  { id: "netbanking", label: "Net Banking", icon: "🏦", desc: "All major banks supported" },
  { id: "cod", label: "Cash on Delivery", icon: "💵", desc: "Pay when you receive" },
  { id: "emi", label: "EMI", icon: "📅", desc: "No Cost EMI available" },
];

const UPI_APPS = ["GPay", "PhonePe", "Paytm", "BHIM", "Amazon Pay"];

export default function CheckoutPayment() {
  const navigate = useNavigate();
  const { items, clearCart, cartTotal, memberSavings } = useCart();
  const [method, setMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [selectedUpiApp, setSelectedUpiApp] = useState(null);
  const [cardData, setCardData] = useState({ num: "", name: "", exp: "", cvv: "" });
  const [placing, setPlacing] = useState(false);
  const [success, setSuccess] = useState(false);

  const selectedItems = items.filter(i => i.selected !== false);
  const total = selectedItems.reduce((s, i) => s + i.price * i.qty, 0);
  const memSave = Math.round(total * 0.09);
  const shipping = total > 445 ? 0 : 50;
  const finalTotal = total - memSave + shipping;

  const handlePlaceOrder = () => {
    setPlacing(true);
    setTimeout(() => {
      setPlacing(false);
      setSuccess(true);
      clearCart();
    }, 2000);
  };

  if (success) {
    return (
      <div className="order-success">
        <div className="order-success__card">
          <div className="order-success__check">✓</div>
          <h2>Order Placed Successfully!</h2>
          <p>Your order has been confirmed. You'll receive a tracking link via SMS & email.</p>
          <p className="order-success__id">Order ID: #TSS{Math.floor(Math.random() * 9000000 + 1000000)}</p>
          <div className="order-success__actions">
            <button className="order-success__track-btn" onClick={() => navigate("/")}>CONTINUE SHOPPING</button>
            <button className="order-success__home-btn" onClick={() => navigate("/")}>GO TO HOME</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="cart-breadcrumb">
        <span className="cart-breadcrumb__step">MY BAG</span>
        <span className="cart-breadcrumb__line" />
        <span className="cart-breadcrumb__step">ADDRESS</span>
        <span className="cart-breadcrumb__line" />
        <span className="cart-breadcrumb__step cart-breadcrumb__step--active">PAYMENT</span>
      </div>

      <div className="checkout-layout container">
        <div className="checkout-form-wrap">
          <h2 className="checkout-section-title">Select Payment Method</h2>

          <div className="payment-methods">
            {PAYMENT_METHODS.map(pm => (
              <div key={pm.id}
                className={`payment-method ${method === pm.id ? "payment-method--active" : ""}`}
                onClick={() => setMethod(pm.id)}>
                <div className="payment-method__radio">
                  <div className={`payment-method__dot ${method === pm.id ? "payment-method__dot--active" : ""}`} />
                </div>
                <div className="payment-method__info">
                  <p className="payment-method__label">{pm.label}</p>
                  <p className="payment-method__desc">{pm.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* UPI */}
          {method === "upi" && (
            <div className="payment-detail">
              <h3 className="payment-detail__title">Pay via UPI App</h3>
              <div className="upi-apps">
                {UPI_APPS.map(app => (
                  <button key={app}
                    className={`upi-app-btn ${selectedUpiApp === app ? "upi-app-btn--active" : ""}`}
                    onClick={() => setSelectedUpiApp(app)}>
                    {app}
                  </button>
                ))}
              </div>
              <p className="payment-detail__or">— OR —</p>
              <div className="upi-id-row">
                <input className="form-input" placeholder="Enter UPI ID (e.g. name@upi)"
                  value={upiId} onChange={e => setUpiId(e.target.value)} />
                <button className="upi-verify-btn">VERIFY</button>
              </div>
            </div>
          )}

          {/* Card */}
          {method === "card" && (
            <div className="payment-detail">
              <h3 className="payment-detail__title">Card Details</h3>
              <div className="checkout-grid">
                <div className="form-field form-field--full">
                  <label className="form-label">Card Number *</label>
                  <input className="form-input" placeholder="0000 0000 0000 0000" maxLength={19}
                    value={cardData.num}
                    onChange={e => setCardData(d => ({ ...d, num: e.target.value.replace(/\D/g, "").replace(/(.{4})/g, "$1 ").trim() }))} />
                </div>
                <div className="form-field form-field--full">
                  <label className="form-label">Cardholder Name *</label>
                  <input className="form-input" placeholder="Name on card"
                    value={cardData.name} onChange={e => setCardData(d => ({ ...d, name: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Expiry Date *</label>
                  <input className="form-input" placeholder="MM/YY" maxLength={5}
                    value={cardData.exp} onChange={e => setCardData(d => ({ ...d, exp: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label className="form-label">CVV *</label>
                  <input className="form-input" placeholder="•••" maxLength={3} type="password"
                    value={cardData.cvv} onChange={e => setCardData(d => ({ ...d, cvv: e.target.value }))} />
                </div>
              </div>
            </div>
          )}

          {/* COD */}
          {method === "cod" && (
            <div className="payment-detail">
              <div className="cod-info">
                <span className="cod-info__icon">💵</span>
                <div>
                  <p className="cod-info__title">Cash on Delivery</p>
                  <p className="cod-info__desc">Pay ₹ {finalTotal.toLocaleString()} when your order arrives. COD charges may apply.</p>
                </div>
              </div>
            </div>
          )}

          {/* Net Banking */}
          {method === "netbanking" && (
            <div className="payment-detail">
              <h3 className="payment-detail__title">Select Your Bank</h3>
              <div className="bank-grid">
                {["SBI", "HDFC", "ICICI", "Axis", "Kotak", "Yes Bank"].map(bank => (
                  <button key={bank} className="bank-btn">{bank}</button>
                ))}
              </div>
              <select className="form-input" style={{ marginTop: 12 }}>
                <option>Select other bank</option>
              </select>
            </div>
          )}

          {/* EMI */}
          {method === "emi" && (
            <div className="payment-detail">
              <h3 className="payment-detail__title">No Cost EMI Options</h3>
              <div className="emi-options">
                {[3, 6, 9, 12].map(months => (
                  <div key={months} className="emi-option">
                    <p className="emi-option__months">{months} Months</p>
                    <p className="emi-option__amount">₹ {Math.round(finalTotal / months).toLocaleString()}/mo</p>
                    <p className="emi-option__note">No cost, no interest</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="payment-secure-note">
            🔒 100% Secure Checkout — Your payment info is encrypted
          </div>

          <button
            className={`checkout-continue-btn ${placing ? "checkout-continue-btn--loading" : ""}`}
            onClick={handlePlaceOrder}
            disabled={placing || items.length === 0}>
            {placing ? (
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <span className="spinner" /> PROCESSING...
              </span>
            ) : `PAY ₹ ${finalTotal.toLocaleString()}.00`}
          </button>
        </div>

        <OrderSummary cartTotal={cartTotal} memberSavings={memberSavings} />
      </div>
    </div>
  );
}
